import React, { useCallback, useEffect, useMemo, useState } from 'react';
import './ControlPanelHeader.scss';
import LanguageSelector from '../LanguageSelector/LanguageSelector';
import ThemeSelector from '../ThemeSelector/ThemeSelector';
import BackendService from '../../services/BackendService';
import { LocalDataTrack } from 'twilio-video';
import useVideoContext from '../../hooks/useVideoContext/useVideoContext';
import { useTranslation } from 'react-i18next';
import { useInterval } from 'react-use';
import useAppContext from '../../hooks/useAppContext/useAppContext';
import { ControlPanelHeaderModal } from './style';
import { hashCurrentSession } from '../../utils/sessionUUIDToCode';
import KeyIcon from '../../assets/mainControls/IconKey.svg?react';

interface ContainerProps {
  practitionerIsConnected: boolean;
  clientIsConnected: boolean;
  callHasStarted: boolean;
  setClientIsConnected: Function;
  setPractitionerIsConnected: Function;
  connectToRoom: Function;
  StopCall: Function;
  sessionId: string;
  treatmentId: string;
  currentState: any;
  showThemeSelector: boolean;
}

const ControlPanelHeader: React.FC<ContainerProps> = ({
  practitionerIsConnected,
  clientIsConnected,
  callHasStarted,
  setClientIsConnected,
  setPractitionerIsConnected,
  connectToRoom,
  sessionId,
  treatmentId,
  StopCall,
  currentState,
  showThemeSelector,
}) => {
  const baseIconUrl = 'assets/icons/';
  const [isConnecting, setIsConnecting] = useState<boolean>(false);
  const [isStopDisabled, setIsStopDisabled] = useState<boolean>(false);
  const { room, localTracks: localVal } = useVideoContext();
  const [token, setToken] = useState<string>('');
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const {
    t,
    i18n: { language },
  } = useTranslation();
  const [totalDuration, setTotalDuration] = useState<number>(0);
  const {
    isConnected,
    headsetWorn,
    wifiQuality,
    wifiSpeed,
    batteryHeadset,
    batteryController1,
    timestamp,
    serialNumber,
  } = currentState || {};

  const { hasDevice } = useAppContext();
  const { onSaveDataModal } = ControlPanelHeaderModal;
  const practitionerHash = hashCurrentSession(sessionId, treatmentId, 'practitioner-hash');
  const deviceMessage = useMemo(() => {
    if (!isConnected) {
      return '';
    }
    return (
      <>
        <p>{headsetWorn ? t('headset-worn') : t('headset-not-worn')}</p>
        <p>
          {t('wifi-quality')}: {wifiQuality}
        </p>
        <p>
          {t('wifi-speed')}: {wifiSpeed} Mbps
        </p>
        <p>
          {t('wifi-quality')}: {batteryHeadset * 100}%
        </p>
        <p>
          {t('battery-controller-1')}: {batteryController1 * 100}%
        </p>
        <p>
          {t('latest-update')}: {new Date(timestamp).toLocaleTimeString()}
        </p>
      </>
    );
  }, [headsetWorn, wifiQuality, wifiSpeed, batteryHeadset, batteryController1, timestamp, isConnected, t]);

  useInterval(() => {
    setTotalDuration(totalDuration + 1);
  }, 1000);

  // Attach listeners for client connection status indicator.
  useEffect(() => {
    const handleParticipantConnected = () => {
      setClientIsConnected(true);
    };
    const handleParticipantDisconnected = () => {
      setClientIsConnected(false);
    };

    room.on('participantConnected', handleParticipantConnected);
    room.on('participantDisconnected', handleParticipantDisconnected);

    return () => {
      room.off('participantConnected', handleParticipantConnected);
      room.off('participantDisconnected', handleParticipantDisconnected);
    };
  }, [room, setClientIsConnected]);

  useEffect(() => {
    if (!callHasStarted) {
      setIsStopDisabled(false);
      return;
    }

    setIsStopDisabled(true);
    const timeoutId = window.setTimeout(() => {
      setIsStopDisabled(false);
    }, 1500);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [callHasStarted]);

  useEffect(() => {
    if (token && localVal.length > 0) {
      const localDataTrack = localVal.find(track => track.kind === 'data') as LocalDataTrack;
      connectToRoom(token, localDataTrack);
      setToken('');
    }
  }, [localVal, token]);

  const copyLink = useCallback(() => {
    const url = `https://emdr-remote.psylaris.nl/clientpage?treatmentUUID=${treatmentId}&sessionUUID=${sessionId}&deviceId=`;
    navigator.clipboard.writeText(url).then(() => {
      setIsSaving(true);
      setTimeout(() => setIsSaving(false), 2000);
    });
  }, [sessionId, treatmentId]);

  const toggleCallHasStarted = async () => {
    setIsConnecting(true);
    const token = await BackendService.getToken(sessionId, treatmentId);
    setToken(token);
    setPractitionerIsConnected(true);
    setIsConnecting(false);
  };

  const stopCall = () => {
    if (isStopDisabled) {
      return;
    }
    StopCall();
    setPractitionerIsConnected(false);
    setClientIsConnected(false);
    setIsConnecting(false);
  };

  const renderCallOptions = useMemo(() => {
    if (isConnecting) {
      return (
        <button className={'loading-button'} data-tooltip={t('connecting') as string}>
          <div />
          <div />
          <div />
        </button>
      );
    }

    if (!callHasStarted) {
      return (
        <button className={'call-button'} onClick={toggleCallHasStarted} data-tooltip={t('start-call') as string}>
          <img src={'assets/icons/start-call.svg'} alt="" />
        </button>
      );
    } else {
      return (
        <button
          className={'call-button stop'}
          onClick={stopCall}
          disabled={isStopDisabled}
          data-tooltip={t('stop-call') as string}
        >
          <img src={'assets/icons/stop-call.svg'} alt="" />
        </button>
      );
    }
  }, [callHasStarted, t, isConnecting, isStopDisabled]);

  const feedbackUrl = useMemo(() => {
    const baseURL = 'https://psylaris.com/';
    switch (language) {
      case 'nl':
        return `${baseURL}/feedback`;
      case 'de':
        return `${baseURL}/d/feedback`;
      case 'en':
      default:
        return `${baseURL}/en/feedback`;
    }
  }, [language]);

  return (
    <div className="header">
      <div className="connection-info">
        {renderCallOptions}
        <img
          src={`${baseIconUrl}${
            practitionerIsConnected ? 'connected-practitioner.svg' : 'disconnected-practitioner.svg'
          }`}
          alt=""
          data-tooltip={t('status-icon-practitioner') as string}
        />
        <img
          src={`${baseIconUrl}${clientIsConnected ? 'connected-user.svg' : 'disconnected-user.svg'}`}
          alt=""
          data-tooltip={t('status-icon-client') as string}
        />
        {hasDevice ? (
          <img
            src={`${baseIconUrl}${isConnected ? 'device_connected.svg' : 'device_not_connected.svg'}`}
            alt=""
            data-tooltip={
              isConnected ? (t('status-icon-device') as string) : `${t('device-not-connected')}: ${serialNumber || ''}`
            }
          />
        ) : null}
      </div>
      <div className="session-info-box">
        <div className="session-info" data-tooltip={t('total-session-duration') as string}>
          <img src={'assets/icons/clock.svg'} alt="" />
          <p>{Math.round(totalDuration / 60 || 0)} min.</p>
        </div>
        <div className="hash-container" onClick={copyLink} data-tooltip={t('sessionID') as string}>
          <KeyIcon className="icon-hash" />
          {practitionerHash}
          {isSaving && <p className="copied-notification">{t('successfully-copied')}</p>}
        </div>
      </div>
      <div className="info-bar">
        {/*<a href={feedbackUrl} target="_blank" rel="noreferrer" data-tooltip={t('info') as string}>*/}
        {/*  <img src={'assets/icons/info-icon.svg'} alt="" />*/}
        {/*</a>*/}
        {showThemeSelector ? <ThemeSelector /> : null}
        <LanguageSelector />
      </div>
    </div>
  );
};

export default ControlPanelHeader;
